import { useState, useRef } from 'react';
import { Todo, TodoStatus } from '../../types/todo';
import { formatDateTime, formatDate } from '../../utils/time';
import { isToday } from '../../utils/calendar';
import { parseTodoDueDate } from '../../utils/todoDate';
import { Icon } from '../../utils/icons';
import { FamilyMember } from '../../store/familyStore';
import { useThemeStore } from '../../store/themeStore';
import { getDesignTokens } from '../../utils/designTokens';
import MemberAvatar from '../MemberAvatar';

interface MobileTodoCardProps {
  todo: Todo;
  onClick: () => void;
  onToggleStatus?: (todoId: string, status: TodoStatus) => void;
  familyMembers?: FamilyMember[];
}

function MobileTodoCard({ 
  todo, 
  onClick,
  onToggleStatus,
  familyMembers = []
}: MobileTodoCardProps) {
  const { isDarkMode } = useThemeStore();
  const tokens = getDesignTokens(isDarkMode);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const touchStartX = useRef<number>(0);
  const touchStartY = useRef<number>(0);
  const isSwiping = useRef<boolean>(false);
  const priorityColors = {
    critical: 'bg-red-500',
    high: 'bg-orange-500',
    medium: 'bg-yellow-500',
    low: 'bg-blue-500',
  };

  const priorityBorderColors = {
    critical: '#ef4444',
    high: '#f97316',
    medium: '#eab308',
    low: '#3b82f6',
  };

  const formatDateShort = (dueDateTime: Date | string | undefined): string => {
    const date = parseTodoDueDate(dueDateTime);
    if (!date) return '';
    if (isToday(date)) {
      return 'Today';
    }
    return formatDate(date, 'short').replace(/, \d{4}$/, '');
  };

  // Check if todo is past due
  const isPastDue = (): boolean => {
    if (!todo.dueDateTime || todo.status === 'done') return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (!todo.dueDateTime) return false;
    // Parse dueDateTime, handling both Date objects and ISO strings
    let dueDate: Date;
    if (todo.dueDateTime instanceof Date) {
      dueDate = todo.dueDateTime;
    } else if (typeof todo.dueDateTime === 'string') {
      const dueStr = todo.dueDateTime;
      if (dueStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = dueStr.split('-').map(Number);
        dueDate = new Date(year, month - 1, day);
      } else {
        const isoDate = new Date(dueStr);
        dueDate = new Date(isoDate.getUTCFullYear(), isoDate.getUTCMonth(), isoDate.getUTCDate());
      }
    } else {
      dueDate = new Date();
    }
    const dueDateOnly = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate());
    return dueDateOnly.getTime() < today.getTime();
  };

  const pastDue = isPastDue();

  // Get assignee member from family members
  const assigneeMember = todo.assigneeId 
    ? familyMembers.find(m => m.userId === todo.assigneeId)
    : null;
  
  // Get assignee color from family members
  const assigneeColor = assigneeMember?.color || '#3b82f6'; // Default blue if no assignee or not found
  
  // Construct member object for MemberAvatar (matching event format)
  const memberForAvatar = todo.assigneeName && assigneeMember
    ? {
        memberId: assigneeMember.userId,
        memberName: assigneeMember.name,
        memberColor: assigneeMember.color,
        memberAvatar: assigneeMember.avatar,
      }
    : todo.assigneeName
    ? {
        memberId: todo.assigneeId,
        memberName: todo.assigneeName,
        memberColor: assigneeColor,
        memberAvatar: undefined,
      }
    : null;

  const [hasSwiped, setHasSwiped] = useState(false);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    isSwiping.current = false;
    setHasSwiped(false);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStartX.current) return;
    
    const currentX = e.touches[0].clientX;
    const currentY = e.touches[0].clientY;
    const deltaX = currentX - touchStartX.current;
    const deltaY = currentY - touchStartY.current;
    
    // Only start swiping if horizontal movement is greater than vertical
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
      isSwiping.current = true;
      setHasSwiped(true);
      e.preventDefault(); // Prevent scrolling while swiping
      
      // Allow swipe right for "open" todos (to mark done)
      if (todo.status === 'open' && deltaX > 0) {
        const maxSwipe = 100;
        setSwipeOffset(Math.min(deltaX, maxSwipe));
      } 
      // Allow swipe left for "done" todos (to mark open)
      else if (todo.status === 'done' && deltaX < 0) {
        const maxSwipe = 100;
        setSwipeOffset(Math.max(deltaX, -maxSwipe));
      }
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const threshold = 60; // Minimum swipe distance to trigger action
    
    if (isSwiping.current && onToggleStatus) {
      if (todo.status === 'open' && swipeOffset > threshold) {
        // Swiped right enough - mark as done
        e.preventDefault();
        e.stopPropagation();
        onToggleStatus(todo.todoId, 'done');
      } else if (todo.status === 'done' && swipeOffset < -threshold) {
        // Swiped left enough - mark as open
        e.preventDefault();
        e.stopPropagation();
        onToggleStatus(todo.todoId, 'open');
      }
    }
    
    // Reset swipe
    setSwipeOffset(0);
    touchStartX.current = 0;
    touchStartY.current = 0;
    isSwiping.current = false;
    
    // Prevent click if we swiped
    if (hasSwiped) {
      e.preventDefault();
      e.stopPropagation();
      setHasSwiped(false);
    }
  };

  const swipePercentage = Math.abs(swipeOffset) / 100;
  const isSwipingRight = swipeOffset > 0;
  const isSwipingLeft = swipeOffset < 0;

  return (
    <div className="relative mb-2 overflow-hidden">
      {/* Background action indicator */}
      {(isSwipingRight || isSwipingLeft) && (
        <div 
          className={`absolute inset-0 ${
            isSwipingRight ? tokens.button.primary : 'bg-gray-400'
          }`}
          style={{ 
            opacity: Math.max(0.7, swipePercentage),
            zIndex: 0
          }}
        >
          <div className={`flex items-center h-full ${
            isSwipingRight ? 'justify-start pl-4' : 'justify-end pr-4'
          }`}>
            {isSwipingRight && (
              <Icon name="check" size="lg" className="text-white" />
            )}
            {isSwipingLeft && (
              <Icon name="arrow-left" size="lg" className="text-white" />
            )}
          </div>
        </div>
      )}
      
      {/* Todo card */}
      <div
        onClick={(e) => {
          if (!hasSwiped && !isSwiping.current) {
            onClick();
          }
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        className={`bg-white rounded-landing p-3 shadow-sm border active:bg-gray-50 min-h-[80px] touch-manipulation relative z-10 transition-transform ${
          pastDue ? 'border-red-300 bg-red-50/30' : 'border-gray-200'
        }`}
        style={{
          borderLeftWidth: '4px',
          borderLeftColor: pastDue ? '#ef4444' : (todo.status === 'done' ? '#9ca3af' : assigneeColor),
          borderRightWidth: todo.priority && todo.priority !== 'low' && todo.status === 'open' ? '4px' : '0px',
          borderRightColor: todo.priority && todo.priority !== 'low' && todo.status === 'open' 
            ? priorityBorderColors[todo.priority] 
            : 'transparent',
          opacity: todo.status === 'done' ? 0.7 : 1,
          transform: `translateX(${swipeOffset}px)`,
        }}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            {/* Title */}
            <div className="mb-1">
              <h3 className={`text-base font-semibold ${
                todo.status === 'done' 
                  ? 'text-gray-500 line-through' 
                  : 'text-gray-900'
              }`}>
                {todo.title}
              </h3>
            </div>

          {/* Description */}
          {todo.description && (
            <p className={`text-sm mb-1 ml-11 ${
              todo.status === 'done' ? 'text-gray-400' : 'text-gray-600'
            }`}>
              {todo.description}
            </p>
          )}

          {/* Due date */}
          {todo.dueDateTime && (
            <div className="flex items-center gap-1.5 mb-1">
              <Icon name="clock" size="sm" className={pastDue ? 'text-red-500' : 'text-gray-500'} />
              <span className={`text-xs ${pastDue ? 'text-red-600 font-semibold' : 'text-gray-600'}`}>
                {pastDue ? 'Past due' : formatDateShort(todo.dueDateTime)}
              </span>
            </div>
          )}

          {/* Event Link */}
          {todo.eventId && todo.eventTitle && (
            <div className="flex items-center gap-1.5 mb-1 ml-11">
              <Icon name="calendar" size="sm" className={tokens.text.accent} />
              <span className={`text-xs font-medium ${tokens.text.accent}`}>
                {todo.eventTitle}
              </span>
            </div>
          )}

          {/* Assignee */}
          {memberForAvatar && (
            <div className="flex items-center gap-1.5 ml-11 flex-wrap">
              <MemberAvatar
                member={memberForAvatar}
                size="sm"
              />
              <span className="text-xs text-gray-600">{memberForAvatar.memberName}</span>
            </div>
          )}
          {todo.isFamily && !todo.assigneeName && (
            <div className="flex items-center gap-1.5 ml-11">
              <span className="text-xs text-gray-500">Family todo</span>
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default MobileTodoCard;
